import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  CookieConsentStatus,
  CookieConsentStorageKey,
} from 'constants/localStorage'
import {
  isGoogleAnalyticsEnabled,
  removeTrackingCookies,
  updateAnalyticsConsent,
} from 'utils/analytics'

interface CookieConsentContextType {
  consentStatus: CookieConsentStatus | null
  isCookieConsentBannerOpen: boolean
  saveConsentStatus: (status: CookieConsentStatus) => void
  openCookieSettings: () => void
}

interface Props {
  children: ReactNode
}

const CookieConsentContext = createContext({} as CookieConsentContextType)

export const useCookieConsent = (): CookieConsentContextType =>
  useContext(CookieConsentContext)

const getStoredConsentStatus = (): CookieConsentStatus | null => {
  try {
    const storedConsentStatus = localStorage.getItem(CookieConsentStorageKey)
    return (
      Object.values(CookieConsentStatus).find(
        (status) => status === storedConsentStatus
      ) ?? null
    )
  } catch {
    // localStorage is unavailable, e.g. blocked by browser privacy settings
    return null
  }
}

const CookieConsentProvider = ({ children }: Props) => {
  const [consentStatus, setConsentStatus] =
    useState<CookieConsentStatus | null>(null)
  const [isStoredConsentStatusLoaded, setIsStoredConsentStatusLoaded] =
    useState(false)
  const [isCookieSettingsOpen, setIsCookieSettingsOpen] = useState(false)

  // localStorage is only available in the browser, not during SSR
  useEffect(() => {
    setConsentStatus(getStoredConsentStatus())
    setIsStoredConsentStatusLoaded(true)
  }, [])

  const saveConsentStatus = useCallback(
    (status: CookieConsentStatus) => {
      try {
        localStorage.setItem(CookieConsentStorageKey, status)
      } catch {
        // Consent then only applies to the current page view
      }
      updateAnalyticsConsent(status)
      setIsCookieSettingsOpen(false)

      if (
        consentStatus === CookieConsentStatus.Granted &&
        status === CookieConsentStatus.Denied
      ) {
        // Already loaded third party scripts can't be unloaded,
        // reload the page without them
        removeTrackingCookies()
        window.location.reload()
        return
      }
      setConsentStatus(status)
    },
    [consentStatus]
  )

  const openCookieSettings = useCallback(() => {
    setIsCookieSettingsOpen(true)
  }, [])

  // Tracking scripts only run in production, so consent is not needed elsewhere
  const isCookieConsentBannerOpen =
    isGoogleAnalyticsEnabled &&
    isStoredConsentStatusLoaded &&
    (consentStatus === null || isCookieSettingsOpen)

  const value = useMemo(
    () => ({
      consentStatus,
      isCookieConsentBannerOpen,
      saveConsentStatus,
      openCookieSettings,
    }),
    [
      consentStatus,
      isCookieConsentBannerOpen,
      saveConsentStatus,
      openCookieSettings,
    ]
  )

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export default CookieConsentProvider
